/**
 * SavedReports
 *
 * Opened from the "Saved reports" toolbar button on the Reports page at
 * /#/infralytiqs/reports/:suiteId/saved-reports. Lists the share artifacts
 * persisted in IL-ReportSuites-Share for the logged-in user's company
 * (currently "Infralytiqs SDK" code snippets saved from the Share dialog).
 *
 * The toolbar (search / sort / tile-table toggle) and the tile & table designs
 * deliberately mirror the Instant Updates "Data Sets" page so both listing
 * screens stay visually consistent.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem, Chip,
  useTheme, IconButton, Button, CircularProgress, Backdrop, Tooltip,
  TextField, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Alert, Snackbar,
} from '@mui/material';
import {
  Delete, Visibility as VisibilityIcon, ViewModule as ViewModuleIcon,
  TableRows as TableRowsIcon, BookmarksOutlined, Close as CloseIcon,
  Code as CodeIcon, ContentCopy, Edit as EditIcon, Preview as PreviewIcon,
  OpenInNew,
} from '@mui/icons-material';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { DataTableWithStaticSchema } from 'components/DataTable/DataTableWithStaticSchema';
import apiClient from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { EditSavedReportDialog } from './EditSavedReportDialog';
import { PreviewSavedReportDialog } from './PreviewSavedReportDialog';

interface SavedShare {
  _id: string;
  companyId: string;
  name: string;
  snippet: string;
  securityType: string;
  reportSuites: string[];
  server: string;
  shareUsername?: string;
  defaultReportSuite?: string;
  theme?: string;
  themeType?: string;
  createdBy: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

const VIEWMODE_STORAGE_KEY = 'saved-reports-view-mode';
const SHARES_ENDPOINT = '/il/share/report-suites';

export const SavedReports: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Same prefix logic as Reports.tsx: this page is reachable from both the
  // /analytics/* (Campaign King) and /infralytiqs/* (Instant Update) menus.
  const routePrefix = location.pathname.startsWith('/analytics')
    ? '/analytics/reports'
    : '/infralytiqs/reports';

  const heroTileFg = theme.palette.dashboard?.heroTileForegroundColor;
  const heroTileLabelColor = theme.palette.dashboard?.heroTileLabelColor
    ?? (heroTileFg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255,255,255,0.7)');
  const heroTileTextShadow = heroTileFg ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
  const tileGradient = theme.palette.dashboard?.counterHeroGradient
    ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;

  // PROFESSIONAL light remaps secondary outlined buttons to low-contrast tones on
  // dialog action bars — use inherit there (same approach as Templates delete dialog).
  const isProfessionalLightDialog = Boolean(
    theme.palette.mode === 'light' && theme.palette.dashboard?.counterHeroGradient,
  );

  const [shares, setShares] = useState<SavedShare[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [tableRefreshTrigger, setTableRefreshTrigger] = useState(0);

  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<string>('desc');

  // View / edit / preview / delete dialogs
  const [viewShare, setViewShare] = useState<SavedShare | null>(null);
  const [editShare, setEditShare] = useState<SavedShare | null>(null);
  const [previewShare, setPreviewShare] = useState<SavedShare | null>(null);
  const [deleteShare, setDeleteShare] = useState<SavedShare | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchShares = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await apiClient.get<SavedShare[]>(SHARES_ENDPOINT);
      setShares(Array.isArray(res.data) ? res.data : []);
    } catch (error: any) {
      Logger.error('SavedReports', `Failed to fetch saved shares: ${error}`);
      setErrorMsg(error?.message || 'Failed to load saved reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchShares(); }, [fetchShares]);

  const filteredAndSortedShares = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = shares.filter((s) => {
      if (!term) return true;
      return (
        s.name?.toLowerCase().includes(term)
        || s.server?.toLowerCase().includes(term)
        || (s.createdByName || s.createdBy || '').toLowerCase().includes(term)
        || (s.reportSuites || []).some((rs) => rs.toLowerCase().includes(term))
      );
    });
    const dir = sortOrder === 'asc' ? 1 : -1;
    filtered.sort((a, b) => {
      if (sortField === 'createdByName') {
        const an = (a.createdByName || a.createdBy || '').toLowerCase();
        const bn = (b.createdByName || b.createdBy || '').toLowerCase();
        return an.localeCompare(bn) * dir;
      }
      const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return (at - bt) * dir;
    });
    return filtered;
  }, [shares, searchTerm, sortField, sortOrder]);

  const handleConfirmDelete = async () => {
    if (!deleteShare) return;
    setDeleting(true);
    try {
      await apiClient.delete(`${SHARES_ENDPOINT}/${deleteShare._id}`);
      setSnackbar({ message: `"${deleteShare.name}" deleted`, severity: 'success' });
      setDeleteShare(null);
      setTableRefreshTrigger((n) => n + 1);
      void fetchShares();
    } catch (error: any) {
      Logger.error('SavedReports', `Failed to delete saved share: ${error}`);
      setSnackbar({ message: error?.message || 'Error deleting saved report', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleCopySnippet = async () => {
    if (!viewShare?.snippet) return;
    try {
      await navigator.clipboard.writeText(viewShare.snippet);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const formatDate = (value?: string) => (value ? new Date(value).toLocaleString() : '—');

  // Static schema for the table view (mirrors the Data Sets page pattern).
  const sharesSchema = useMemo(() => ({
    paths: {
      _id: { instance: 'String', isRequired: false },
      name: { instance: 'String', isRequired: false },
      server: { instance: 'String', isRequired: false },
      reportSuites: { instance: 'Array', isRequired: false },
      securityType: { instance: 'String', isRequired: false },
      createdByName: { instance: 'String', isRequired: false },
      createdAt: { instance: 'Date', isRequired: false },
    },
  }), []);

  const tableMandatoryColumns = useMemo(
    () => ['name', 'server', 'reportSuites', 'securityType', 'createdByName', 'createdAt'],
    [],
  );

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
      {loading && (
        <Backdrop
          sx={(t: any) => ({ color: t.palette.secondary.main, zIndex: t.zIndex.drawer + 1 })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <DynamicBreadcrumbs items={[
        { itemPath: routePrefix, itemLabel: 'Report Dashboard' },
        { itemPath: '', itemLabel: 'Saved Reports' },
      ]} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" gutterBottom>
          Saved Reports
        </Typography>
      </Box>

      {/* Toolbar: search (left), sort + view toggle (right) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)'),
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 180 }}>
          <TextField
            label="Search Saved Reports"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: '200px' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortField}
              label="Sort By"
              onChange={(e) => setSortField(e.target.value as string)}
            >
              <MenuItem value="createdByName">Created By</MenuItem>
              <MenuItem value="createdAt">Created Date</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              label="Order"
              onChange={(e) => setSortOrder(e.target.value as string)}
            >
              <MenuItem value="asc">ASC</MenuItem>
              <MenuItem value="desc">DESC</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
              localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
            }
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <ToggleButton value="tile" aria-label="Tile view">
            <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      {!loading && !errorMsg && shares.length === 0 && (
        <Alert severity="info" icon={<BookmarksOutlined />}>
          No saved reports yet. Use the Share dialog on the Report Dashboard
          (Infralytiqs SDK → Save, or Credentials → Generate link) to save one.
        </Alert>
      )}

      {/* Tile View */}
      {viewMode === 'tile' && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2.5}
          sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}
        >
          {filteredAndSortedShares.map((share) => (
            <Box
              key={share._id}
              onClick={() => setViewShare(share)}
              sx={{
                width: 260,
                height: 200,
                borderRadius: 2.5,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover .tile-overlay': { height: 120 },
                '&:hover .tile-details': { opacity: 1 },
                '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                '@media (hover: none)': {
                  '& .tile-overlay': { height: 120 },
                  '& .tile-details': { opacity: 1 },
                  '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                },
              }}
            >
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: tileGradient,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              }}>
                <CodeIcon sx={{ fontSize: 40, color: heroTileFg ?? '#fff', opacity: 0.85 }} />
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: heroTileFg ?? '#fff', lineHeight: 1, textShadow: heroTileTextShadow }}>
                  {share.reportSuites?.length ?? 0}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: heroTileLabelColor, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Report Suites
                </Typography>
              </Box>

              <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
                <Chip
                  label={share.server}
                  color="success"
                  size="small"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', maxWidth: 120 }}
                />
                <Chip
                  label={share.securityType}
                  color="warning"
                  size="small"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                />
              </Box>

              <Box
                className="tile-overlay"
                sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  overflow: 'hidden',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                  transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: 40, px: 1.5,
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                }}
              >
                <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                  color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                  lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                  transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                }}>
                  {share.name}
                </Typography>

                <Box className="tile-details" sx={{
                  opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                  display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                }}>
                  <Typography variant="subtitle2" sx={{
                    color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3,
                    textShadow: '0 1px 3px rgba(0,0,0,0.4)', wordBreak: 'break-word',
                  }}>
                    {share.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                    Created by: {share.createdByName || share.createdBy || 'Unknown'}
                  </Typography>
                  {share.createdAt && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                      Created: {new Date(share.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="View">
                        <Button color="secondary" variant="contained" size="small"
                          onClick={(e) => { e.stopPropagation(); setViewShare(share); }}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                          <VisibilityIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button color="secondary" variant="contained" size="small"
                          onClick={(e) => { e.stopPropagation(); setEditShare(share); }}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                          <EditIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                      <Tooltip title={share.securityType === 'Credentials' ? 'Open link' : 'Preview'}>
                        <Button color="secondary" variant="contained" size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (share.securityType === 'Credentials' && share.snippet) {
                              window.open(share.snippet, '_blank', 'noopener,noreferrer');
                              return;
                            }
                            setPreviewShare(share);
                          }}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                          {share.securityType === 'Credentials' ? <OpenInNew fontSize="small" /> : <PreviewIcon fontSize="small" />}
                        </Button>
                      </Tooltip>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Delete">
                        <Button color="error" variant="contained" size="small"
                          onClick={(e) => { e.stopPropagation(); setDeleteShare(share); }}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                          <Delete fontSize="small" />
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Box sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          p: 2,
        }}>
          <DataTableWithStaticSchema
            key={`${sortField}-${sortOrder}`}
            dataUrl={SHARES_ENDPOINT}
            schemaObject={sharesSchema}
            columnsToExclude={['_id']}
            refreshTrigger={tableRefreshTrigger}
            initialSortColumn={sortField}
            initialSortOrder={sortOrder as 'asc' | 'desc'}
            dataSetName="saved-reports-list"
            mandatoryColumns={tableMandatoryColumns}
            externalSearchQuery={searchTerm}
            hideSearch
            columnRenderers={{
              reportSuites: (row: any) => (Array.isArray(row.reportSuites) ? row.reportSuites.join(', ') : ''),
              securityType: (row: any) => (
                <Chip label={row.securityType} color="warning" variant="outlined" size="small" />
              ),
              createdAt: (row: any) => formatDate(row.createdAt),
            }}
            customActions={(row: any) => {
              const isCredentials = row.securityType === 'Credentials';
              return [
              {
                label: 'View',
                onClick: (r: any) => setViewShare(r as SavedShare),
                color: 'secondary',
                icon: <VisibilityIcon />,
              },
              {
                label: 'Edit',
                onClick: (r: any) => setEditShare(r as SavedShare),
                color: 'secondary' as const,
                icon: <EditIcon />,
              },
              {
                label: isCredentials ? 'Open' : 'Preview',
                onClick: (r: any) => {
                  if (isCredentials && r.snippet) {
                    window.open(r.snippet, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  setPreviewShare(r as SavedShare);
                },
                color: 'secondary',
                icon: isCredentials ? <OpenInNew /> : <PreviewIcon />,
              },
              {
                label: 'Delete',
                onClick: (r: any) => setDeleteShare(r as SavedShare),
                color: 'error',
                icon: <Delete />,
              },
            ];
            }}
          />
        </Box>
      )}

      {/* View dialog: all fields read-only */}
      <Dialog
        open={Boolean(viewShare)}
        onClose={() => { setViewShare(null); setCopied(false); }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookmarksOutlined fontSize="small" />
            <Typography variant="h6" component="span" fontWeight={600}>
              {viewShare?.name}
            </Typography>
          </Box>
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => { setViewShare(null); setCopied(false); }} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Name" value={viewShare?.name || ''} size="small" InputProps={{ readOnly: true }} sx={{ flex: '1 1 220px' }} />
            <TextField label="ClickHouse Server" value={viewShare?.server || ''} size="small" InputProps={{ readOnly: true }} sx={{ flex: '1 1 220px' }} />
            <TextField label="Security Type" value={viewShare?.securityType || ''} size="small" InputProps={{ readOnly: true }} sx={{ flex: '1 1 220px' }} />
          </Box>
          <TextField
            label="Report Suites"
            value={(viewShare?.reportSuites || []).join(', ')}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
          />
          {viewShare?.defaultReportSuite && (
            <TextField
              label="Default Report Suite"
              value={viewShare.defaultReportSuite}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Created By" value={viewShare?.createdByName || viewShare?.createdBy || ''} size="small" InputProps={{ readOnly: true }} sx={{ flex: '1 1 220px' }} />
            <TextField label="Created" value={formatDate(viewShare?.createdAt)} size="small" InputProps={{ readOnly: true }} sx={{ flex: '1 1 220px' }} />
            <TextField label="Updated" value={formatDate(viewShare?.updatedAt)} size="small" InputProps={{ readOnly: true }} sx={{ flex: '1 1 220px' }} />
          </Box>
          {viewShare?.securityType === 'Credentials' && (
            <TextField
              label="Shareable username"
              value={viewShare?.shareUsername || ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              helperText="Password is stored hashed and cannot be displayed. Use Edit to reset it."
            />
          )}
          <TextField
            label={viewShare?.securityType === 'Credentials' ? 'Shareable URL' : 'Code snippet'}
            value={viewShare?.snippet || ''}
            size="small"
            fullWidth
            multiline
            minRows={viewShare?.securityType === 'Credentials' ? 2 : 6}
            maxRows={16}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
            onFocus={(e) => e.target.select()}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<ContentCopy />} onClick={() => void handleCopySnippet()} variant="outlined" color="secondary">
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            {viewShare?.securityType === 'Credentials' && viewShare?.snippet && (
              <Button
                size="small"
                startIcon={<OpenInNew />}
                variant="outlined"
                color="secondary"
                onClick={() => window.open(viewShare.snippet, '_blank', 'noopener,noreferrer')}
              >
                Open
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <EditSavedReportDialog
        open={Boolean(editShare)}
        onClose={() => setEditShare(null)}
        share={editShare}
        onSaved={() => {
          setSnackbar({ message: `"${editShare?.name}" updated`, severity: 'success' });
          setTableRefreshTrigger((n) => n + 1);
          void fetchShares();
        }}
      />

      <PreviewSavedReportDialog
        open={Boolean(previewShare)}
        onClose={() => setPreviewShare(null)}
        share={previewShare}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(deleteShare)}
        onClose={() => !deleting && setDeleteShare(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>Delete saved report?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete
            {' '}
            <strong>{deleteShare?.name}</strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            {...(isProfessionalLightDialog ? { color: 'inherit' as const } : { color: 'secondary' as const })}
            onClick={() => setDeleteShare(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'success'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SavedReports;
